/**
 * Cashback-specific form state sub-hook.
 * Manages: prime restriction, T&C, guardrails, rounding option.
 */

import { useState, useEffect } from 'react';
import { SelectProps } from '@amzn/awsui-components-core/select';
import { RoundingOption } from 'src/@types/valueMatching/enums';
import { FormMode } from 'src/components/organisms/CampaignForm/types';
import { CampaignData } from 'src/@types/valueMatching/campaign';

export interface UseCashbackFormStateParams {
    mode: FormMode;
    initialData?: CampaignData;
}

/** Rounding options — constant defined outside the component to avoid re-creation on every render. */
const ROUNDING_OPTIONS: SelectProps.Option[] = Object.values(RoundingOption).map(
    (roundingValue) => ({ label: roundingValue, value: roundingValue }),
);

export function useCashbackFormState({ mode, initialData }: UseCashbackFormStateParams) {
    const [primeRestriction, setPrimeRestriction] = useState(false);
    const [termsAndConditions, setTermsAndConditions] = useState('');
    const [cashbackExtentGuardrail, setCashbackExtentGuardrail] = useState('100');
    const [cashbackPercentLimitGuardrail, setCashbackPercentLimitGuardrail] = useState('');
    const [cashbackAbsoluteLimitGuardrail, setCashbackAbsoluteLimitGuardrail] = useState('');
    const [roundingOption, setRoundingOption] = useState('');

    // ── Populate from initialData (modify / view) ───────────────────────
    useEffect(() => {
        if (mode === FormMode.CREATE || !initialData) return;

        const cashbackAttrs = initialData.attributes?.cashback;
        if (cashbackAttrs) {
            setPrimeRestriction(cashbackAttrs.primeRestriction ?? false);
            setTermsAndConditions(cashbackAttrs.termsAndConditions ?? '');
            setRoundingOption(cashbackAttrs.roundToNearest ?? '');
        }

        const cashbackGuard = initialData.guardrails?.cashback;
        if (cashbackGuard) {
            setCashbackExtentGuardrail(
                String(cashbackGuard.percentExtentOfCompetitorGuardrail ?? '100'),
            );
            setCashbackPercentLimitGuardrail(
                String(cashbackGuard.percentOfASINPriceGuardrail ?? ''),
            );
            setCashbackAbsoluteLimitGuardrail(
                String(cashbackGuard.absoluteValueGuardrail?.currencyValue ?? ''),
            );
        }
    }, [mode, initialData]);

    return {
        primeRestriction,
        setPrimeRestriction,
        termsAndConditions,
        setTermsAndConditions,
        cashbackExtentGuardrail,
        setCashbackExtentGuardrail,
        cashbackPercentLimitGuardrail,
        setCashbackPercentLimitGuardrail,
        cashbackAbsoluteLimitGuardrail,
        setCashbackAbsoluteLimitGuardrail,
        roundingOption,
        setRoundingOption,
        roundingOptions: ROUNDING_OPTIONS,
    };
}
